"use client";

import { useEffect } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, Home, RefreshCw } from "lucide-react";
import { logger } from "@/lib/logger";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log the error (can be extended to send to error tracking service like Sentry)
    logger.error("Application error", { error: error.message, digest: error.digest, stack: error.stack });
  }, [error]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-12 pb-8">
          <div className="flex flex-col items-center text-center space-y-6">
            {/* Icon */}
            <div className="rounded-full bg-error/10 dark:bg-red-900/20 p-6">
              <AlertTriangle className="h-16 w-16 text-error dark:text-error" />
            </div>

            {/* Error Message */}
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-2">
                Something Went Wrong
              </h1>
              <h2 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-3">
                An unexpected error occurred
              </h2>
              {process.env.NODE_ENV === "development" && (
                <p className="text-sm text-gray-600 dark:text-gray-400 font-mono bg-gray-100 dark:bg-gray-800 p-3 rounded-md max-w-sm overflow-auto">
                  {error.message}
                </p>
              )}
            </div>

            {/* Description */}
            <p className="text-gray-600 dark:text-gray-400 max-w-sm">
              We&apos;re sorry for the inconvenience. Please try reloading the
              page or return to the home page.
            </p>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 w-full pt-4">
              <Button
                onClick={reset}
                className="flex-1"
                size="lg"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
              <Link href="/dashboard" className="flex-1">
                <Button variant="outline" className="w-full" size="lg">
                  <Home className="h-4 w-4 mr-2" />
                  Go to Dashboard
                </Button>
              </Link>
            </div>

            {/* Footer */}
            {error.digest && (
              <div className="pt-6 border-t border-gray-200 dark:border-gray-700 w-full">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Error ID: {error.digest}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
